import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/cn';
import type {
  TransparencyStep,
} from '../../types/transparency';
import {
  stepLabel,
  stepResultSummary,
  sourceKindForStep,
  sourceLabelForStep,
  isSynthesisStep,
  kindColor,
} from '../../lib/transparency-narratives';
import { SOURCE_ICONS } from './source-icons';

// Public interface

export interface ReasoningTimelineProps {
  steps: TransparencyStep[];
  variant: 'compact' | 'detailed';
  debugMode?: boolean;
  /** When provided, evidence sections with >5 items show a "See all" link */
  accountId?: string;
}

// Variant-specific sizing tokens

const VARIANT = {
  compact: {
    gap: 'gap-2',
    pb: 'pb-3',
    diamondSize: 'h-[7px] w-[7px]',
    lineLeft: 'left-[4px]',
    lineTop: 'top-3.5',
    titleText: 'text-xs font-medium text-foreground/80 dark:text-nightshade-200/80',
    resultText: 'text-xs text-muted-foreground dark:text-nightshade-400',
    sourceText: 'text-[10px] text-muted-foreground/70 dark:text-nightshade-400/70',
    sourceGap: 'gap-1',
    sourceMt: 'mt-0.5',
    evidenceMt: 'mt-1',
    evidenceText: 'text-[10px]',
    evidenceIcon: 'h-2.5 w-2.5',
    evidenceListMt: 'mt-0.5',
    evidenceListSpace: 'space-y-0',
    evidenceItemPl: 'pl-3',
    evidenceItemGap: 'gap-0.5',
    evidenceItemIcon: 'h-2.5 w-2.5',
  },
  detailed: {
    gap: 'gap-3',
    pb: 'pb-6',
    diamondSize: 'h-[9px] w-[9px]',
    lineLeft: 'left-[5px]',
    lineTop: 'top-4',
    titleText: 'font-medium text-sm',
    resultText: 'text-sm text-muted-foreground dark:text-nightshade-400 mt-0.5',
    sourceText: 'text-xs text-muted-foreground dark:text-nightshade-400',
    sourceGap: 'gap-1.5',
    sourceMt: 'mt-1',
    evidenceMt: 'mt-2',
    evidenceText: 'text-xs',
    evidenceIcon: 'h-3 w-3',
    evidenceListMt: 'mt-1',
    evidenceListSpace: 'space-y-0.5',
    evidenceItemPl: 'pl-4',
    evidenceItemGap: 'gap-1.5',
    evidenceItemIcon: 'h-3 w-3',
  },
} as const;

// Timeline step

function TimelineStep({
  step,
  isLast,
  variant,
  debugMode,
  accountId,
}: {
  step: TransparencyStep;
  isLast: boolean;
  variant: 'compact' | 'detailed';
  debugMode: boolean;
  accountId?: string;
}) {
  const [evidenceExpanded, setEvidenceExpanded] = useState(false);
  const kind = sourceKindForStep(step);
  const label = sourceLabelForStep(step);
  const synthesis = isSynthesisStep(step);
  const v = VARIANT[variant];

  return (
    <div className={cn('relative flex', v.gap, v.pb)}>
      {!isLast && (
        <div
          className={cn(
            'absolute bottom-0 w-px bg-border dark:bg-nightshade-700',
            v.lineLeft,
            v.lineTop,
          )}
        />
      )}
      <div
        className={cn(
          'mt-1.5 shrink-0 rotate-45 border',
          v.diamondSize,
          kindColor(step),
        )}
      />
      <div className="flex-1 min-w-0">
        {variant === 'detailed' ? (
          <div className="flex items-center gap-2">
            <p className={v.titleText}>
              {step.narrativeAction ?? stepLabel(step)}
            </p>
            {synthesis && (
              <span className="inline-flex items-center rounded-full bg-pink-500/10 px-2 py-0.5 text-[10px] font-medium text-pink-600">
                Summary
              </span>
            )}
          </div>
        ) : (
          <p className={v.titleText}>
            {step.narrativeAction ?? stepLabel(step)}
          </p>
        )}
        <p className={v.resultText}>
          {step.narrativeResult ?? stepResultSummary(step)}
        </p>
        <div
          className={cn('flex items-center', v.sourceGap, v.sourceMt, v.sourceText)}
        >
          {SOURCE_ICONS[kind]}
          {step.type === 'retrieval' && (step).source.url ? (
            <a
              href={(step).source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground dark:hover:text-nightshade-100 hover:underline flex items-center gap-0.5"
            >
              {label}
              <ExternalLink className="h-2.5 w-2.5 opacity-50" />
            </a>
          ) : (
            <span>{label}</span>
          )}
        </div>

        {/* Evidence items — collapsed by default */}
        {step.evidenceItems != null && step.evidenceItems.length > 0 && (
          <div className={v.evidenceMt}>
            <button
              onClick={() => setEvidenceExpanded((prev) => !prev)}
              className={cn(
                'text-muted-foreground dark:text-nightshade-400 hover:text-foreground dark:hover:text-nightshade-100 flex items-center',
                v.evidenceText,
                v.evidenceItemGap,
              )}
            >
              <ChevronRight
                className={cn(
                  'transition-transform',
                  v.evidenceIcon,
                  evidenceExpanded && 'rotate-90',
                )}
              />
              Evidence ({step.evidenceItems.length})
            </button>
            {evidenceExpanded && (() => {
              const total = step.evidenceItems.length;
              const showSeeAll = total > 5 && accountId;
              const visible = showSeeAll ? step.evidenceItems.slice(0, 5) : step.evidenceItems;
              const sourceIdentifier =
                step.type === 'retrieval'
                  ? (step).source.identifier
                  : null;

              return (
                <ul
                  className={cn(
                    'text-muted-foreground dark:text-nightshade-400',
                    v.evidenceText,
                    v.evidenceListMt,
                    v.evidenceListSpace,
                  )}
                >
                  {visible.map((item, i) => (
                    <li
                      // eslint-disable-next-line react/no-array-index-key -- evidence item labels may repeat; composite with index disambiguates
                      key={`${item.label}-${i}`}
                      className={cn(
                        'flex items-center',
                        v.evidenceItemPl,
                        v.evidenceItemGap,
                      )}
                    >
                      {item.url ? (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            'hover:text-foreground dark:hover:text-nightshade-100 hover:underline flex items-center',
                            v.evidenceItemGap,
                          )}
                        >
                          {item.label}
                          <ExternalLink className={v.evidenceItemIcon} />
                        </a>
                      ) : (
                        <span>{item.label}</span>
                      )}
                    </li>
                  ))}
                  {showSeeAll && sourceIdentifier && (
                    <li
                      className={cn(
                        'flex items-center',
                        v.evidenceItemPl,
                        v.evidenceItemGap,
                      )}
                    >
                      <Link
                        to={`/accounts/${accountId}/data/${sourceIdentifier}`}
                        className="text-blue-600 hover:text-blue-800 dark:text-blueberry-400 dark:hover:text-blueberry-300 hover:underline"
                      >
                        See all {total} &rarr;
                      </Link>
                    </li>
                  )}
                </ul>
              );
            })()}
          </div>
        )}

        {/* Result items — detailed variant only */}
        {variant === 'detailed' &&
          step.type === 'retrieval' &&
          (step).resultItems &&
          (step).resultItems.length > 0 && (
            <div className="mt-2 max-h-40 overflow-y-auto rounded border dark:border-nightshade-700 bg-muted/30 dark:bg-nightshade-800/30 p-2 space-y-0.5">
              {(step).resultItems.map((item, i) => (
                <p
                  // eslint-disable-next-line react/no-array-index-key -- result item labels may repeat; composite with index disambiguates
                  key={`${item.label}-${i}`}
                  className="text-xs text-muted-foreground dark:text-nightshade-400 leading-relaxed"
                >
                  {item.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-foreground dark:hover:text-nightshade-100"
                    >
                      {item.label}
                    </a>
                  ) : (
                    item.label
                  )}
                </p>
              ))}
            </div>
          )}

        {/* Debug mode: tier 3 technical details */}
        {debugMode && (
          <div className="mt-2 rounded border border-dashed border-muted-foreground/30 dark:border-nightshade-500/30 bg-muted/20 dark:bg-nightshade-800/20 p-2 space-y-1">
            <p className="text-[10px] font-mono text-muted-foreground/60 dark:text-nightshade-400/60">
              type={step.type} | tier={step.tier} | start={step.startMs}ms |
              duration={step.durationMs}ms
            </p>
            {step.type === 'retrieval' && (step).source.query && (
              <pre className="text-[10px] font-mono text-muted-foreground/50 dark:text-nightshade-400/50 whitespace-pre-wrap max-h-24 overflow-auto">
                {(step).source.query}
              </pre>
            )}
            {step.type === 'generation' && (
              <p className="text-[10px] font-mono text-muted-foreground/50 dark:text-nightshade-400/50">
                model={(step).model} | in=
                {(step).inputTokens} | out=
                {(step).outputTokens}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ReasoningTimeline — the main export

export default function ReasoningTimeline({
  steps,
  variant,
  debugMode = false,
  accountId,
}: ReasoningTimelineProps) {
  const maxTier = debugMode ? 3 : 2;
  const visibleSteps = steps.filter((s) => s.tier <= maxTier);
  if (visibleSteps.length === 0) return null;

  const sorted = [...visibleSteps].sort((a, b) => a.startMs - b.startMs);

  if (sorted.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No provenance steps recorded for this section.
      </p>
    );
  }

  return (
    <>
      {sorted.map((step, i) => (
        <TimelineStep
          // eslint-disable-next-line react/no-array-index-key -- composite key includes step type and startMs; index tiebreaker for identical timestamps
          key={`${step.type}-${step.startMs}-${i}`}
          step={step}
          isLast={i === sorted.length - 1}
          variant={variant}
          debugMode={debugMode}
          accountId={accountId}
        />
      ))}
    </>
  );
}
